import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster as Sonner, toast, type ToasterProps } from 'sonner';
import { Check, Copy, X, XCircle } from 'lucide-react';

import { useTheme } from '@/shared/hooks/useTheme';
import type { DescribedError } from '@/shared/lib/errors';
import { Button } from './button';

export function Toaster(props: ToasterProps): JSX.Element {
  const { theme } = useTheme();
  return (
    <Sonner
      theme={theme}
      className="toaster group"
      visibleToasts={3}
      duration={4000}
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:rounded-xl group-[.toaster]:bg-popover group-[.toaster]:text-popover-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
}

interface ErrorToastProps {
  id: string | number;
  error: DescribedError;
  onRetry?: () => void;
}

// Neutral popover surface, red icon, what failed + what next + reference code
// (DESIGN.md §8). Stays until closed: an error the user didn't read is lost.
export function ErrorToast({ id, error, onRetry }: ErrorToastProps): JSX.Element {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const copy = (): void => {
    if (!error.ref) return;
    void navigator.clipboard?.writeText(error.ref).then(() => setIsCopied(true));
  };
  return (
    <div
      role="alert"
      className="flex w-[356px] max-w-[calc(100vw-32px)] gap-3 rounded-xl border bg-popover p-4 text-popover-foreground shadow-lg"
    >
      <XCircle className="mt-0.5 size-4 shrink-0 text-destructive-text" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium">{error.title}</p>
        {error.hint ? <p className="text-sm text-muted-foreground">{error.hint}</p> : null}
        {error.ref ? (
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 rounded text-xs text-muted-foreground hover:text-foreground"
            aria-label={t('errors.copyRef')}
          >
            <span>
              {t('errors.ref')}: <span className="select-all font-mono">{error.ref}</span>
            </span>
            {isCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          </button>
        ) : null}
        {error.devDetails ? (
          <p className="break-all rounded bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">
            {error.devDetails}
          </p>
        ) : null}
        {onRetry ? (
          <div className="pt-2">
            <Button
              variant="outline"
              onClick={() => {
                toast.dismiss(id);
                onRetry();
              }}
            >
              {t('errors.retry')}
            </Button>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => toast.dismiss(id)}
        aria-label={t('common.close')}
        className="-mr-1 -mt-1 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
